'use client';

import type { ReactNode } from 'react';
import CommonSpacer from '@/components/sections/shared/CommonSpacer';
import ProductPpcIntro from '@/components/sections/Products/ProductPpcIntro';
import ProductPpcApplications from '@/components/sections/Products/ProductPpcApplications';
import ProductSustainabilitySplit from '@/components/sections/Products/ProductSustainabilitySplit';
import WitnessFarqEngineering from '@/components/sections/Products/WitnessFarqEngineering';
import ProductSpecializedRange from '@/components/sections/Products/ProductSpecializedRange';
import ProductsHero from '@/components/sections/Products/ProductsHero';
import ProductBenefitsFaqTabsShell from '../components/ProductBenefitsFaqTabsShell';
import { stripHtml } from '@/lib/homepage-mappers';
import { resolveProductDetailHref } from '@/lib/product-urls';
import { resolveStrapiMediaUrl } from '@/lib/strapi-media-url';
import { cn } from '@/utils/cn';
import type {
  ProductInnerBenefitsComponent,
  ProductInnerType,
  ProductInnerZoneComponent,
  VideoSectionComponent,
} from '@/types/product';

interface ProductDetailViewProps {
  locale: string;
  slug: string;
  productInner: ProductInnerType;
  faqSlot?: ReactNode;
}

function normalizeBenefitsSection(component: ProductInnerBenefitsComponent) {
  return {
    heading: component.heading,
    subHeading: component.subHeading,
    largeMedia: component.benefitList?.largeMedia ?? component.media,
    smallMedia: component.benefitList?.smallMedia,
    benefitsList: component.benefitList?.benefits ?? component.list,
    faqImage: component.productInnerFaq?.faqImage,
    faqs: component.productInnerFaq?.faqs,
    helpText: component.productInnerFaq?.helpText,
    connectButton: component.productInnerFaq?.button?.[0],
  };
}

function getFullMediaUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  return resolveStrapiMediaUrl(url) ?? undefined;
}

function isSpacerZone(zone: ProductInnerZoneComponent | undefined): boolean {
  if (!zone) return false;
  return (
    zone.__component === 'common.spacer-top' ||
    zone.__component === 'common.spacer-full' ||
    zone.__component === 'common.spacer-bottom'
  );
}

function zoneWillRender(zone: ProductInnerZoneComponent, faqSlot?: ReactNode): boolean {
  switch (zone.__component) {
    case 'products.benefits-section': {
      const benefitsSection = normalizeBenefitsSection(zone);
      return Boolean(benefitsSection.benefitsList?.length || benefitsSection.faqs?.length);
    }
    case 'products.zigzag-section':
      return Boolean(zone.item?.length);
    case 'modules.faqwith-form':
      return Boolean(faqSlot);
    case 'products.about-product':
    case 'products.uses-application':
    case 'modules.video-sectionwith-heading':
    case 'products.similar-products':
    case 'common.spacer-top':
    case 'common.spacer-full':
    case 'common.spacer-bottom':
      return true;
    default:
      return false;
  }
}

function findNeighborZone(
  zones: ProductInnerZoneComponent[],
  startIndex: number,
  direction: -1 | 1,
  faqSlot?: ReactNode,
): ProductInnerZoneComponent | undefined {
  for (
    let index = startIndex + direction;
    direction === -1 ? index >= 0 : index < zones.length;
    index += direction
  ) {
    const zone = zones[index];
    if (!zoneWillRender(zone, faqSlot)) continue;
    return zone;
  }
  return undefined;
}

function shouldRenderProductZone(
  zones: ProductInnerZoneComponent[],
  index: number,
  faqSlot?: ReactNode,
): boolean {
  const zone = zones[index];
  if (!zoneWillRender(zone, faqSlot)) return false;

  if (!isSpacerZone(zone)) return true;

  const previousZone = findNeighborZone(zones, index, -1, faqSlot);
  const nextZone = findNeighborZone(zones, index, 1, faqSlot);

  if (!previousZone || !nextZone) return false;

  if (previousZone.__component === 'modules.video-sectionwith-heading') return false;

  if (
    nextZone.__component === 'products.uses-application' &&
    (zone.__component === 'common.spacer-top' || zone.__component === 'common.spacer-full')
  ) {
    return false;
  }

  return true;
}

function prepareProductZones(
  zones: ProductInnerZoneComponent[],
  faqSlot?: ReactNode,
): ProductInnerZoneComponent[] {
  return zones.filter((_, index) => shouldRenderProductZone(zones, index, faqSlot));
}

function getSpecializedRangeSectionClassName(
  zones: ProductInnerZoneComponent[],
  index: number,
): string {
  const hasSpacerBefore = Boolean(zones[index - 1] && isSpacerZone(zones[index - 1]));
  const hasSpacerAfter = Boolean(zones[index + 1] && isSpacerZone(zones[index + 1]));

  return cn(
    !hasSpacerBefore && 'pt-10 sm:pt-12 md:pt-14',
    !hasSpacerAfter && 'pb-10 sm:pb-12 md:pb-14',
  );
}

function renderProductZone(
  zone: ProductInnerZoneComponent,
  locale: string,
  slug: string,
  faqSlot?: ReactNode,
  zones: ProductInnerZoneComponent[] = [],
  zoneIndex = -1,
) {
  switch (zone.__component) {
    case 'common.spacer-top':
    case 'common.spacer-full':
    case 'common.spacer-bottom':
      return <CommonSpacer key={zone.id} zone={zone} />;

    case 'products.about-product':
      return (
        <ProductPpcIntro
          key={zone.id}
          locale={locale}
          heading={zone.heading}
          subHeading={zone.subHeading}
          description={zone.description}
          buttons={zone.button}
          bgImage={zone.bgImage}
          productImage={zone.productImage}
        />
      );

    case 'products.benefits-section': {
      const benefitsSection = normalizeBenefitsSection(zone);
      if (!benefitsSection.benefitsList?.length && !benefitsSection.faqs?.length) return null;

      return (
        <ProductBenefitsFaqTabsShell
          key={zone.id}
          locale={locale}
          benefits={{
            heading: benefitsSection.heading,
            subHeading: benefitsSection.subHeading,
            media: benefitsSection.largeMedia,
            smallMedia: benefitsSection.smallMedia,
            benefitsList: benefitsSection.benefitsList,
          }}
          faq={{
            faqs: benefitsSection.faqs,
            faqImage: benefitsSection.faqImage,
            helpText: benefitsSection.helpText,
            connectButton: benefitsSection.connectButton,
          }}
        />
      );
    }

    case 'products.uses-application':
      return (
        <ProductPpcApplications
          key={zone.id}
          heading={zone.heading}
          subHeading={zone.subHeading}
          gridItems={zone.gridItem}
        />
      );

    case 'products.zigzag-section':
      if (!zone.item?.length) return null;
      return (
        <ProductSustainabilitySplit
          key={zone.id}
          items={zone.item}
          heading={zone.heading}
          subHeading={zone.subHeading}
        />
      );

    case 'modules.video-sectionwith-heading':
      return (
        <WitnessFarqEngineering
          key={zone.id}
          videoComponent={zone as VideoSectionComponent}
          theme="light"
          sectionClassName="py-0"
        />
      );

    case 'products.similar-products':
      return (
        <ProductSpecializedRange
          key={zone.id}
          sectionClassName={getSpecializedRangeSectionClassName(zones, zoneIndex)}
          heading={zone.heading}
          subheading={zone.description}
          items={
            zone.productCard
              ?.map((card) => {
                const imageSrc = getFullMediaUrl(card.media?.url);
                if (!imageSrc) return null;

                return {
                  id: String(card.id),
                  imageSrc,
                  title: card.heading,
                  description: stripHtml(card.description),
                  href: resolveProductDetailHref(card.urlOptional, locale, card.heading),
                };
              })
              .filter((item): item is NonNullable<typeof item> => item !== null) ?? []
          }
        />
      );

    case 'modules.faqwith-form':
      return faqSlot ? <div key={zone.id}>{faqSlot}</div> : null;

    default:
      return null;
  }
}

export default function ProductDetailView({
  locale,
  slug,
  productInner,
  faqSlot,
}: ProductDetailViewProps) {
  const aboutComponent = productInner.productZone?.find(
    (zone) => zone.__component === 'products.about-product',
  );
  const productDescription =
    aboutComponent && 'description' in aboutComponent ? aboutComponent.description : undefined;

  const zones = prepareProductZones(productInner.productZone ?? [], faqSlot);

  return (
    <div className="min-h-screen bg-white">
      <ProductsHero
        title={productInner.productTitle}
        subtitle={productInner.productSubtitle}
        description={productDescription ?? ''}
        bannerMedia={productInner.bannerMedia}
        bannerMediaPoster={productInner.bannerMediaPosterOptional}
      />

      {zones.map((zone, index) => renderProductZone(zone, locale, slug, faqSlot, zones, index))}
    </div>
  );
}
